import { Tooltip } from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';

import { FormatType } from '../../types';
import { getFormatter } from '../../utilities';
import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import styles from './chord-chart.module.css';
import type { CardinalPlacement } from './chord-tooltip-position';
import type { ChordFlowMode, ChordShareDirection } from './types';

type ChordRibbonTooltipProps = PropsWithChildren & {
  sourceLabel: string;
  targetLabel: string;
  flow: number;
  share: number | undefined;
  shareDirection: ChordShareDirection;
  isOpen: boolean;
  placement: CardinalPlacement;
  flowMode: ChordFlowMode;
};

const format = getFormatter(FormatType.INTEGER);
const percentFormat = getFormatter(FormatType.PERCENTAGE);

export const ChordRibbonTooltip = ({
  sourceLabel,
  targetLabel,
  flow,
  share,
  shareDirection,
  isOpen,
  placement,
  flowMode,
  children,
}: ChordRibbonTooltipProps) => {
  return (
    <Tooltip
      label={
        <TooltipContent
          sourceLabel={sourceLabel}
          targetLabel={targetLabel}
          flow={flow}
          share={share}
          shareDirection={shareDirection}
          flowMode={flowMode}
        />
      }
      placement={placement}
      padding="8px 12px"
      borderRadius="4px"
      hasArrow
      isOpen={isOpen}
      maxW="none"
      data-testid="chord-chart__ribbon-tooltip"
      className={tooltipStyles.surface}
    >
      {children}
    </Tooltip>
  );
};

const TooltipContent = ({
  sourceLabel,
  targetLabel,
  flow,
  share,
  shareDirection,
  flowMode,
}: {
  sourceLabel: string;
  targetLabel: string;
  flow: number;
  share: number | undefined;
  shareDirection: ChordShareDirection;
  flowMode: ChordFlowMode;
}) => {
  const isNet = flowMode === 'net';
  const flowLabel = isNet ? 'Net Transitions' : 'Transitions';
  // Shares are computed against the gross row/column total and don't map to
  // the residual net value, so we drop them entirely in net flow mode.
  const showShare = !isNet && share !== undefined && share > 0;
  const shareLabel =
    shareDirection === 'inflows' ? 'Inflow share' : 'Outflow share';
  return (
    <div className={styles.tooltipBlock}>
      <div className={styles.tooltipTitle}>
        {sourceLabel}
        <span className={styles.tooltipArrow} aria-hidden>
          →
        </span>
        {targetLabel}
      </div>
      <dl className={styles.tooltipMetrics}>
        <dt>{flowLabel}</dt>
        <dd>{format(flow)}</dd>
        {showShare && (
          <>
            <dt>{shareLabel}</dt>
            <dd>{percentFormat(share)}</dd>
          </>
        )}
      </dl>
    </div>
  );
};
